import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import {
  CopilotStudioEvaluationDetailsError,
  type CopilotStudioEvaluationDetailsApi,
} from "../api/copilot-studio-evaluation-details-api.js";
import { GUID_PATTERN } from "../utils/security.js";

export const getCopilotStudioEvaluationDetailsInputSchema = z
  .object({
    agentId: z.string().regex(GUID_PATTERN),
    runId: z.string().regex(GUID_PATTERN),
    environment: z
      .string()
      .regex(/^(?:Default-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
      .optional(),
    includeResponseText: z.boolean().optional(),
    maxResponseChars: z.number().int().min(1).max(4000).optional(),
  })
  .strict();

export const getCopilotStudioEvaluationDetailsTool: Tool = {
  name: "get_copilot_agent_evaluation_details",
  description:
    "EXPERIMENTAL: read one existing Studio evaluation run owned by the current maker using the environment-discovered management gateway. Metadata only by default; includeResponseText=true allows bounded, redacted, untrusted answers only when the service returns Full result visibility and owner identity matches. Never returns prompts, attachments, raw traces or grants. Does not start an evaluation, execute an agent, change connections, sign in, publish or bypass a denial. Requires an already authorized same-user Copilot Studio management token; unavailable authorization fails closed. This observed Studio route is not the documented public Power Platform evaluation API.",
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source.replaceAll("[0-9a-f]", "[0-9a-fA-F]"),
        description: "Exact Dataverse bot GUID.",
      },
      runId: {
        type: "string",
        pattern: GUID_PATTERN.source.replaceAll("[0-9a-f]", "[0-9a-fA-F]"),
        description: "Exact existing evaluation run GUID owned by the current maker.",
      },
      environment: {
        type: "string",
        pattern:
          "^(?:[Dd][Ee][Ff][Aa][Uu][Ll][Tt]-)?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
        description:
          "Exact environment GUID, including Default- prefix when present; defaults to the session environment.",
      },
      includeResponseText: { type: "boolean", default: false },
      maxResponseChars: {
        type: "integer",
        minimum: 1,
        maximum: 4000,
        default: 1000,
        description: "Total redacted answer characters across returned cases.",
      },
    },
    required: ["agentId", "runId"],
    additionalProperties: false,
  },
};

export interface CopilotStudioEvaluationDetailsToolContext {
  detailsApi: CopilotStudioEvaluationDetailsApi | null;
}

export async function handleGetCopilotStudioEvaluationDetails(
  context: CopilotStudioEvaluationDetailsToolContext,
  input: unknown,
) {
  const parsed = getCopilotStudioEvaluationDetailsInputSchema.parse(input);
  if (!context.detailsApi)
    throw new CopilotStudioEvaluationDetailsError(
      "API unavailable; no fallback was attempted.",
      503,
    );
  const result = await context.detailsApi.getEvaluationDetails(parsed.agentId, parsed.runId, {
    environmentId: parsed.environment,
    includeResponseText: parsed.includeResponseText,
    maxResponseChars: parsed.maxResponseChars,
  });
  return {
    text: `Experimental Studio evaluation details read. State: ${result.state ?? "unavailable"}; visibility: ${result.resultViewScope}; returned cases: ${result.returnedTestCaseCount ?? "unavailable"}. This is not proof of successful agent or ticket execution.`,
    structured: { evaluationDetails: result },
  };
}
